import React, { useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../UserContext'
import { getUserReservationData } from '../../../services/userService'
// import requestStatus from '../../../util/requestStatus'
import useUserReservationsReducer, { statusTypes, actionTypes } from './useUserReservationsReducer'
import ReservationsWithSharedTypeTable from '../../reservation/ReservationsWithSharedTypeTable'

/*  The infinite scroll needs:
 *      1. reducer - useUserReservationsReducer (separate file)
 *      2. BottomBorder
 *      3. InteresectionObserver to observe the BottomBorder
 *
 *  The SCROLL_LIMIT defines how many reservations I will  "infinite scroll."
 *  After that the user should download the full reservations list.
 *
 *  The RESERVATION_SET_SIZE specifies how many reservations I fetch each time.
 */

export const SCROLL_LIMIT = 1000
export const RESERVATION_SET_SIZE = 150

function UserReservationsInfiniteScroll({
  handleCheck,
  handleReservations,
  handleTotalPersonsNotShared,
  handleTotalPersonsShared,
  showOnlyCompletedOrdinances,
  // setFetchStatus,
}) {
  const { currentUser } = useContext(UserContext)
  const { contributorId, metadata } = currentUser
  const userName = metadata.header
  const userNames = {}
  userNames[contributorId] = userName
  const [t] = useTranslation()

  const reservationInitialState = {
    status: statusTypes.idle,
    reservationSetTotal: RESERVATION_SET_SIZE,
    scrollLimit: SCROLL_LIMIT,
    contributorId,
    showOnlyCompletedOrdinances: showOnlyCompletedOrdinances || false,
    payload: {
      userReservations: { reservations: [] },
      reservationIndex: 0,
    },
  }

  const { state, dispatch } = useUserReservationsReducer(reservationInitialState)
  if (state !== undefined && state.showOnlyCompletedOrdinances !== showOnlyCompletedOrdinances) {
    dispatch({ type: actionTypes.reset, payload: { showOnlyCompletedOrdinances } })
  }
  useEffect(() => {
    let isFetching = true

    const fetchReservations = async () => {
      // If there is no contributorId - there are no reservations.
      if (isFetching && state.contributorId === null) {
        return dispatch({ type: actionTypes.gotNone })
      }

      const response = await getUserReservationData(
        state.contributorId,
        state.payload.reservationIndex,
        state.reservationSetTotal,
        state.showOnlyCompletedOrdinances
      )

      switch (response.status) {
        case 200: {
          const reservations = response.data.reservations
          handleReservations(response.data)
          handleTotalPersonsNotShared(response.data.totalReservationNotShared)
          handleTotalPersonsShared(response.data.totalReservationShared)
          // User has no reservations
          if (response.data.hasMoreReservations === null && state.payload.reservationIndex === 0) {
            return dispatch({ type: actionTypes.gotNone })
          }

          // We already got all the user's reservations
          if (reservations === null || !response.data.hasMoreReservations) {
            return dispatch({ type: actionTypes.reachedEnd, payload: { userReservations: response.data } })
          }
          // We already got SCROLL_LIMIT worth of user reservations
          if (reservations.reservationIndex + reservations.length >= SCROLL_LIMIT) {
            return dispatch({ type: actionTypes.gotLimit, payload: { userReservations: response.data } })
          }
          // Yeah! We got a set of reservations
          return dispatch({ type: actionTypes.fetchSuccess, payload: { userReservations: response.data } })
        }
        default: {
          // We failed
          return dispatch({ type: actionTypes.fetchError })
        }
      }
    }
    if (currentUser && state.status === statusTypes.loading) {
      // setFetchStatus(requestStatus.STARTED)
      fetchReservations(state, dispatch)
    }

    return function cleanup() {
      isFetching = false
    }
  }, [currentUser, dispatch, state, handleReservations, handleTotalPersonsNotShared, handleTotalPersonsShared])

  const observeBottom = useCallback(
    (node) => {
      if (node !== null) {
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio === 1) {
                if (state.status === statusTypes.idle) {
                  dispatch({ type: actionTypes.fetchStart })
                }
              }
            })
          },
          { threshold: 1 }
        ).observe(node)
      }
    },
    [state, dispatch]
  )

  const renderBottomBorder = () => <div ref={observeBottom} />

  const renderLoadingMessage = () => <div>{t('loading', 'Loading...')}</div>

  const renderNoMoreReservationsMessage = () => (
    <div>
      {t('reservations.retrievedAll', 'Retrieved all reservations:')}{' '}
      {state.payload.userReservations.reservations.length}
    </div>
  )

  const renderNoReservationsMessage = () => <div>{t('reservations.notFound', 'No reservations found.')}</div>

  const renderLimitMessage = () => (
    <div>
      {t('reservations.downloadFullList', 'Download the reservations for a complete list.')}{' '}
      {t('reservations.retrieved', 'Reservations retrieved: ')}
      {state.payload.userReservations.reservations.length}
    </div>
  )

  const foundReservations = () => state.payload !== undefined && state.payload.userReservations.reservations.length > 0

  return (
    <>
      {foundReservations() && (
        <ReservationsWithSharedTypeTable
          reservations={state.payload.userReservations.reservations}
          personData={state.payload.userReservations.personData}
          userNames={userNames}
          setCheckedRanIds={handleCheck}
        />
      )}
      {state.status === statusTypes.loading && renderLoadingMessage()}
      {state.status === statusTypes.noMoreReservations && renderNoMoreReservationsMessage()}
      {state.status === statusTypes.limit && renderLimitMessage()}
      {state.status === statusTypes.noReservations && renderNoReservationsMessage()}
      {renderBottomBorder()}
    </>
  )
}

export default UserReservationsInfiniteScroll
